import { useMutation, useQuery } from "@tanstack/react-query";

import {
  api,
  queryClient,
  type Annotation,
  type ConversationSummary,
  type DocumentOut,
  type DrawingConfig,
  type DrawingOut,
  type EdgeOut,
  type FolderTree,
  type MarkupConfig,
  type DriveStatus,
  type GraphOntology,
  type GraphOverview,
  type NoteCardOut,
  type Neighborhood,
  type NodeOut,
  type OrbitLayout,
  type ProjectOut,
  type ReaderPayload,
  type SearchHit,
  type UsageSummary,
  type VaultTree,
  type WorkspaceItem,
} from "@/lib/api";

export const keys = {
  overview: ["overview"] as const,
  ontology: ["graph", "ontology"] as const,
  orbits: ["orbits"] as const,
  documents: ["documents"] as const,
  document: (id: string) => ["document", id] as const,
  reader: (id: string) => ["reader", id] as const,
  annotations: (id: string) => ["annotations", id] as const,
  markup: (id: string) => ["markup", id] as const,
  drawing: (id: string) => ["drawing", id] as const,
  node: (id: string) => ["node", id] as const,
  search: (q: string) => ["search", q] as const,
  usage: ["usage", "day"] as const,
  workspace: ["workspace"] as const,
  projects: ["projects"] as const,
  conversations: ["conversations"] as const,
  /** The shared PREFIX for every view of the filesystem — the disk-mirroring
   * vault tree AND the four-category folder tree, with or without files.
   *
   * They live under one prefix on purpose. Nineteen call sites across the app
   * already invalidate `keys.vault` after anything that changes the vault
   * (upload, ingest, rename, delete, note creation, wikilink autolink…), and
   * when the explorer moved onto `/api/folders` every one of them would
   * otherwise have needed a second line — with the twentieth forgotten, and an
   * uploaded file simply never appearing in the rail. Prefix matching makes all
   * of them correct at once. */
  vault: ["vault"] as const,
  vaultTree: ["vault", "tree"] as const,
  folders: ["vault", "folders"] as const,
  /** The same tree WITH each folder's files — what the explorer draws. */
  foldersWithItems: ["vault", "folders", "items"] as const,
  notes: (facets: string) => ["notes", facets] as const,
  noteCard: (ref: string) => ["notes", "card", ref] as const,
  metadata: (kind: string, id: string) => ["metadata", kind, id] as const,
  drive: ["drive"] as const,
  health: ["health"] as const,
  cluster: (id: string) => ["cluster", id] as const,
  billing: ["billing", "status"] as const,
  config: ["config"] as const,
};

/** One feed card, for the atlas's hover card.
 *
 * The same card the homepage draws, addressed by id — see `GET
 * /api/notes/cards`. Cached until the note changes and never refetched on
 * focus: a hover must feel instant the second time, and sweeping back and
 * forth across a cluster must not become a request per pixel.
 */
export function useNoteCard(ref: string | null) {
  return useQuery({
    queryKey: keys.noteCard(ref ?? ""),
    queryFn: async () => {
      const page = await api.request<{ items: NoteCardOut[] }>(
        "GET",
        `/api/notes/cards?ref=${encodeURIComponent(ref!)}`,
      );
      return page.items[0] ?? null;
    },
    enabled: !!ref,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** The edge matrix. Static for the life of a build, so it is cached hard —
 * refetching it on every atlas mount would be a round trip for an answer that
 * cannot have changed. */
export function useGraphOntology() {
  return useQuery({
    queryKey: keys.ontology,
    queryFn: () => api.request<GraphOntology>("GET", "/api/graph/ontology"),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useOverview() {
  return useQuery({
    queryKey: keys.overview,
    queryFn: () => api.request<GraphOverview>("GET", "/api/graph/overview"),
    // A 401/403 means "signed out" (show the sign-in card), not a fault to retry
    // — surface it at once. Transient faults (network blip, Fly cold-start 5xx)
    // get a couple of tries so a momentary outage never paints the backend-down
    // card on its way to the real state.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
  });
}

/** The orbit graphing system: the computed systems (rings, planetary belts,
 * polygon clusters), the visible edges between placed nodes/centers, and the
 * `dirty` flag that tells the studio the layout is stale after a connection
 * edit. Positions are computed server-side; the client paints them directly. */
export function useOrbits() {
  return useQuery({
    queryKey: keys.orbits,
    queryFn: () => api.request<OrbitLayout>("GET", "/api/orbits"),
  });
}

/** Recompute the whole orbit set from the current graph (wipes + rebuilds the
 * systems, classifies satellites / planetary / polygon clusters, preserves
 * prior angles where possible). Clears `dirty`. */
export function useRecomputeOrbits() {
  return useMutation({
    mutationFn: () => api.request<OrbitLayout>("POST", "/api/orbits/recompute"),
    onSuccess: (layout) => {
      queryClient.setQueryData(keys.orbits, layout);
      void queryClient.invalidateQueries({ queryKey: keys.orbits });
    },
  });
}

export function useDocuments() {
  return useQuery({
    queryKey: keys.documents,
    queryFn: () => api.request<DocumentOut[]>("GET", "/api/documents"),
  });
}

export function useDocument(id: string | undefined, opts?: { poll?: boolean }) {
  return useQuery({
    queryKey: keys.document(id ?? "?"),
    queryFn: () => api.request<DocumentOut>("GET", `/api/documents/${id}`),
    enabled: !!id,
    refetchInterval: opts?.poll ? 1500 : false,
  });
}

/** Publish (make public) or unpublish (make private) a single document. Docs are
 * private by default; flipping to "public" opts this one document into every
 * atlas surface — manifest, content, search, and the hosted site's chat RAG.
 * Patches the cached document so the toggle reflects instantly, then revalidates
 * the doc + the list (the file tree reads visibility for its badge). */
export function useSetDocumentVisibility(id: string) {
  return useMutation({
    mutationFn: (visibility: "public" | "private") =>
      api.request<{ id: string; visibility: "public" | "private" }>(
        "PATCH",
        `/api/documents/${id}/visibility`,
        { visibility },
      ),
    onSuccess: (res) => {
      queryClient.setQueryData<DocumentOut>(keys.document(id), (prev) =>
        prev ? { ...prev, visibility: res.visibility } : prev,
      );
      void queryClient.invalidateQueries({ queryKey: keys.document(id) });
      void queryClient.invalidateQueries({ queryKey: keys.documents });
    },
  });
}

export function useReader(docId: string) {
  return useQuery({
    queryKey: keys.reader(docId),
    queryFn: () => api.request<ReaderPayload>("GET", `/api/documents/${docId}/reader`),
  });
}

export function useAnnotations(docId: string) {
  return useQuery({
    queryKey: keys.annotations(docId),
    queryFn: () => api.request<NodeOut[]>("GET", `/api/documents/${docId}/annotations`),
  });
}

/** Classic-engine markup (ink/text/highlight rows) for a document.
 *
 * This fetch SEEDS the InkModel, which is thereafter the in-session source of
 * truth for rendering (editors + overlay read from it, not this cache). So the
 * query only needs to load once per open — a generous `staleTime` stops it
 * refetching the whole stroke set on every window focus / remount. ReaderTab
 * keeps this cache honest cheaply from batch results instead of refetching. */
export function useMarkup(docId: string | undefined) {
  return useQuery({
    queryKey: keys.markup(docId ?? "?"),
    queryFn: () => api.request<Annotation[]>("GET", `/api/documents/${docId}/markup`),
    enabled: !!docId,
    staleTime: 5 * 60_000,
  });
}

/** Classic-engine capability flag + allowed fonts (drives the edit toolbar). */
export function useMarkupConfig() {
  return useQuery({
    queryKey: ["markup", "config"] as const,
    queryFn: () => api.request<MarkupConfig>("GET", "/api/markup/config"),
    staleTime: Infinity,
  });
}

/** The document's Excalidraw drawing (empty scene until something is drawn).
 *
 * This fetch SEEDS the editor, which is thereafter the in-session source of
 * truth (its saves flow through the SaveController, which keeps this cache
 * honest via setQueryData) — so a generous `staleTime` stops it refetching the
 * whole scene on every window focus / remount. */
export function useDrawing(docId: string | undefined) {
  return useQuery({
    queryKey: keys.drawing(docId ?? "?"),
    queryFn: () => api.request<DrawingOut>("GET", `/api/documents/${docId}/drawing`),
    enabled: !!docId,
    staleTime: 5 * 60_000,
  });
}

/** Drawing capability flag (hides the annotate affordances when off). */
export function useDrawingConfig() {
  return useQuery({
    queryKey: ["drawing", "config"] as const,
    queryFn: () => api.request<DrawingConfig>("GET", "/api/drawing/config"),
    staleTime: Infinity,
  });
}

export function useNode(id: string | undefined) {
  return useQuery({
    queryKey: keys.node(id ?? "?"),
    queryFn: () => api.request<NodeOut>("GET", `/api/nodes/${id}`),
    enabled: !!id,
  });
}

export function useExpand() {
  return (nodeId: string) =>
    api.request<Neighborhood>("GET", `/api/graph/expand?node_id=${encodeURIComponent(nodeId)}`);
}

export function useSearch(q: string) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () =>
      api.request<{ query: string; hits: SearchHit[] }>(
        "GET",
        `/api/search?q=${encodeURIComponent(q)}&limit=14`,
      ),
    enabled: q.trim().length >= 2,
    staleTime: 5_000,
  });
}

export function useUsage() {
  return useQuery({
    queryKey: keys.usage,
    queryFn: () => api.request<UsageSummary>("GET", "/api/usage/summary?window=day"),
    refetchInterval: 30_000,
  });
}

export function useWorkspace() {
  return useQuery({
    queryKey: keys.workspace,
    queryFn: () => api.request<WorkspaceItem[]>("GET", "/api/workspace/items"),
  });
}

export function useProjects() {
  return useQuery({
    queryKey: keys.projects,
    queryFn: () => api.request<ProjectOut[]>("GET", "/api/projects"),
  });
}

export function useConversations() {
  return useQuery({
    queryKey: keys.conversations,
    queryFn: () => api.request<ConversationSummary[]>("GET", "/api/conversations"),
  });
}

export function useVaultTree() {
  return useQuery({
    queryKey: keys.vaultTree,
    queryFn: () => api.request<VaultTree>("GET", "/api/vault/tree"),
    // Idle-poll fallback only — the paths that actually change the vault
    // (ingest, folder create, upload) already invalidate `keys.vault`
    // explicitly, so this just catches anything else (e.g. a second device
    // writing into the vault). `refetchIntervalInBackground` defaults to
    // false, so this pauses automatically while the tab is hidden.
    refetchInterval: 30_000,
  });
}

/** The four-category folder tree, with colours and item counts. Distinct from
 * `useVaultTree`, which mirrors the DISK (Papers/ + Markdown/). */
export function useFolderTree() {
  return useQuery({
    queryKey: keys.folders,
    queryFn: () => api.request<FolderTree>("GET", "/api/folders"),
    staleTime: 30_000,
  });
}

/** The folder tree WITH the files in each folder — the file explorer's read.
 * The same endpoint as `useFolderTree`, asked for the heavier payload, so both
 * surfaces are looking at one tree rather than two that can disagree. */
export function useFolderTreeWithItems() {
  return useQuery({
    queryKey: keys.foldersWithItems,
    queryFn: () => api.request<FolderTree>("GET", "/api/folders?items=true"),
    staleTime: 15_000,
  });
}

export function useDriveStatus() {
  return useQuery({
    queryKey: keys.drive,
    queryFn: () => api.request<DriveStatus>("GET", "/api/auth/google/status"),
    staleTime: 60_000,
  });
}

export function useHealth() {
  return useQuery({
    queryKey: keys.health,
    queryFn: () => api.health(),
    // Idle heartbeat; `refetchIntervalInBackground` defaults to false, so this
    // pauses automatically while the tab is hidden instead of polling forever.
    refetchInterval: 30_000,
    retry: false,
  });
}

/** Cluster detail. `edges` is NOT empty: the route returns the intra-cluster
 * edge set (services/graph_service.py filters `edges_touching` down to edges
 * whose BOTH ends are in the cluster), and `cluster` is a full ClusterOut with
 * `summary` and `size`. */
export function useClusterDetail() {
  return (clusterId: string) =>
    api.request<{
      cluster: { id: string; label: string; summary: string | null; size: number };
      nodes: NodeOut[];
      edges: EdgeOut[];
    }>("GET", `/api/clusters/${clusterId}`);
}
